import React from 'react';
import { Button, Grid, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormikHelpers } from 'formik';
import moment from 'moment';
import { Add, Clear } from '@mui/icons-material';
import { EventScheduleData, EventScheduleSlotData, PropelUserModel } from '../../api';
import SharedMobileTimePicker from '../../common/ui/SharedMobileTimePicker';
import { UsersMultiSelect } from '../../calendar/components/UsersMultiSelect';

export type EventScheduleFormValues = {
  event_schedule: EventScheduleData;
  event_schedule_apply_from: string;
};

export type EventScheduleDay = Exclude<keyof EventScheduleData, 'generated_until'>;

const DAYS: EventScheduleDay[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

type EventScheduleProps = {
  eventSchedule: EventScheduleData;
  setFieldValue: FormikHelpers<EventScheduleFormValues>['setFieldValue'];
  isSubmitting: boolean;
  readOnly: boolean;
  users: PropelUserModel[];
  locationId: string | null;
};

export const EventSchedule = ({
  eventSchedule,
  setFieldValue,
  isSubmitting,
  readOnly,
  users,
  locationId,
}: EventScheduleProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const formIsDisabled = readOnly || isSubmitting;

  const updateDay = (day: EventScheduleDay, nextSlots: EventScheduleSlotData[]) => {
    void setFieldValue(`event_schedule.${day}`, nextSlots);
  };

  return (
    <Grid container flexDirection="column" gap={3}>
      {DAYS.map((day) => {
        const slots = eventSchedule[day] || [];
        return (
          <Grid
            key={day}
            container
            gap={2}
            alignItems="flex-start"
            flexDirection={isSmallScreen ? 'column' : 'row'}
            flexWrap="nowrap"
            sx={
              isSmallScreen
                ? { border: '1px solid', borderColor: 'mb.control', borderRadius: '6px', p: 1.5 }
                : undefined
            }>
            <Typography fontWeight={600} width={100} color="text.secondary">
              {t(`days.${day}`)}
            </Typography>
            <Grid container flexDirection="column" gap={1}>
              {slots.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('eventSchedule.emptyDay')}
                </Typography>
              )}
              {slots.map((slot, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Grid container gap={1} alignItems="center" key={`${day}-slot-${i}`}>
                  <SharedMobileTimePicker
                    sx={{ width: 120 }}
                    size="small"
                    value={moment.utc(0).add(slot.start_time || 0, 'seconds')}
                    disabled={formIsDisabled}
                    onChange={(newValue) => {
                      if (!newValue) return;
                      const next = [...slots];
                      next[i] = {
                        ...slot,
                        start_time: newValue.hours() * 3600 + newValue.minutes() * 60,
                      };
                      updateDay(day, next);
                    }}
                  />
                  <UsersMultiSelect
                    users={users}
                    size="small"
                    minWidth={220}
                    locationId={locationId}
                    selectedUsersIds={slot.employee_ids || []}
                    setSelectedUsersIds={(ids) => {
                      const next = [...slots];
                      next[i] = { ...slot, employee_ids: ids };
                      updateDay(day, next);
                    }}
                  />
                  <IconButton
                    aria-label="remove-slot"
                    disabled={formIsDisabled}
                    onClick={() =>
                      updateDay(
                        day,
                        slots.filter((_, j) => j !== i),
                      )
                    }>
                    <Clear />
                  </IconButton>
                </Grid>
              ))}
              <Grid item>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={<Add />}
                  disabled={formIsDisabled}
                  onClick={() => updateDay(day, [...slots, { start_time: 32400, employee_ids: [] }])}>
                  {t('eventSchedule.addSlot')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        );
      })}
    </Grid>
  );
};
